import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { DateToLocale, ToFirstCapital } from "../../Utils/helpers";
import { SendAgentNotification } from "../../Redux/Actions/agentActions";
import { AgentListView, UpdateAgentStatus } from "../../Redux/Actions/agentActions";
import MainLayout from "../../Layout/mainLayout";
import toast from "react-hot-toast";
import "./viewPartnerRoles.css";

const ViewPartner = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const [partnerData, setPartnerData] = useState(null);
    const [pageLoading, setPageLoading] = useState(false);
    const [statusValue, setStatusValue] = useState("");
    const [statusUpdating, setStatusUpdating] = useState(false);
    const [blockReason, setBlockReason] = useState("");
    const [showBlockReason, setShowBlockReason] = useState(false);

    useEffect(() => {
        if (partnerData?.account_status) {
            setStatusValue(partnerData.account_status);
        }
    }, [partnerData]);

    const handleStatusSelect = (e) => {
        const value = e.target.value;
        setStatusValue(value);
        if (value === "BLOCK") {
            setShowBlockReason(true);
        } else {
            setShowBlockReason(false);
            setBlockReason("");
        }
    };

    const handleStatusUpdate = async () => {
        if (!statusValue) return toast.error("Please select a status");
        // if (statusValue === "BLOCK" && !blockReason.trim()) {
        //     return toast.error("Please enter a reason for blocking");
        // }

        try {
            setStatusUpdating(true);
            const payload = { id, account_status: statusValue };
            if (statusValue === "BLOCK") payload.reason = blockReason;
            const res = await dispatch(UpdateAgentStatus(payload));
            if (res?.statusCode === 200) {
                setPartnerData((prev) => ({ ...prev, status: statusValue }));
                setShowBlockReason(false);
                setBlockReason("");

                if (statusValue === "approved") {
                    dispatch(SendAgentNotification({
                        agent_id: id,
                        title: "Account Approved",
                        message: "Your Habitat Editor account has been approved by admin.",
                        icon: "",
                        is_read: false,
                        role: "exclusive partner",
                        firebase_token: partnerData?.firebase_token || "none",
                        type_name: "PARTNER_STATUS",
                        type: "PARTNER_STATUS",
                        type_id: id,
                        type_data: {
                            status: "approved",
                            staff_id: id
                        }
                    }));
                }
                // toast.success("Status updated successfully");
            }
        } catch {
            toast.error("Something went wrong");
        } finally {
            setStatusUpdating(false);
        }
    };

    useEffect(() => {
        const fetchPartner = async () => {
            try {
                setPageLoading(true);
                const res = await dispatch(AgentListView(id));
                if (res && res.statusCode === 200) {
                    setPartnerData(res.data);
                }
            } catch {
                // handle error
            } finally {
                setPageLoading(false);
            }
        };
        fetchPartner();
    }, [id, dispatch]);

    if (pageLoading) {
        return (
            <MainLayout>
                <div className="vp-loader-wrap">
                    <div className="vp-loader" />
                    <p className="vp-loader-text">Loading partner data…</p>
                </div>
            </MainLayout>
        );
    }

    if (!partnerData) {
        return (
            <MainLayout>
                <div className="vp-empty-wrap">
                    <div className="vp-empty-icon">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10" />
                            <path d="M12 8v4M12 16h.01" />
                        </svg>
                    </div>
                    <p className="vp-empty-text">No Partner Data Found.</p>
                    <Link to="/partner_roles" className="vp-back-link">← Back to Partners</Link>
                </div>
            </MainLayout>
        );
    }

    const roleLabel = (role) => {
        const map = {
            "exclusive partner": "Exclusive Partner",
            "channel partner": "Channel Partner",
            "referral": "Referral",
            "habitat editor": "Habitat Editor",
        };
        return map[role] || ToFirstCapital(role || "");
    };

    return (
        <MainLayout>
            <div className="vp-wrapper">

                {/* Header */}
                <div className="vp-header">
                    <div className="vp-header-left">
                        <Link to="/partner_roles" className="vp-back-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            Back
                        </Link>
                        <div className="vp-header-divider" />
                        <div>
                            <div className="vp-breadcrumb">Partners / List / View</div>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                        <div className={`vp-status-badge vp-status-badge--${partnerData?.status?.toLowerCase()}`}>
                            {ToFirstCapital(partnerData?.account_status || "")}
                        </div>
                        {partnerData?.role && (
                            <div className="vp-role-badge">
                                {roleLabel(partnerData.role)}
                            </div>
                        )}
                    </div>
                </div>

                {/* Hero Card */}
                <div className="vp-hero-card">
                    <div className="vp-hero-left">
                        {partnerData?.profile_image ? (
                            <img
                                src={partnerData.profile_image}
                                alt={partnerData.name}
                                className="vp-hero-img"
                            />
                        ) : (
                            <div className="vp-hero-avatar">
                                {partnerData?.name?.charAt(0)?.toUpperCase() || "P"}
                            </div>
                        )}
                        <div>
                            <div className="vp-hero-label">Partner Name</div>
                            <div className="vp-hero-value">{partnerData?.name || "—"}</div>
                            {partnerData?.email && (
                                <div className="vp-hero-sub">{partnerData.email}</div>
                            )}
                        </div>
                    </div>
                    <div className="vp-hero-meta">
                        {(partnerData?.country_code || partnerData?.contact_number) && (
                            <div className="vp-hero-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.36 12 19.79 19.79 0 0 1 1.21 3.58 2 2 0 0 1 3.22 1.4h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16.92z" />
                                </svg>
                                {partnerData?.country_code} {partnerData?.contact_number}
                            </div>
                        )}
                        {partnerData?.staff_id && (
                            <div className="vp-hero-meta-item">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="2" y="7" width="20" height="14" rx="2" />
                                    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
                                </svg>
                                {partnerData.staff_id}
                            </div>
                        )}
                        <div className="vp-hero-meta-item">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            {DateToLocale(partnerData?.created_at) || "—"}
                        </div>
                    </div>
                </div>

                {/* Grid Layout */}
                <div className="vp-grid-layout">

                    {/* Left Column */}
                    <div className="vp-col">

                        {/* Partner Information */}
                        <div className="vp-card">
                            <div className="vp-card-header">
                                <span className="vp-card-badge">01</span>
                                <h2 className="vp-card-title">Partner Information</h2>
                            </div>
                            <div className="vp-info-grid">
                                <div className="vp-info-item">
                                    <span className="vp-info-label">Full Name</span>
                                    <span className="vp-info-value">{partnerData?.name || "No Data"}</span>
                                </div>
                                <div className="vp-info-item">
                                    <span className="vp-info-label">Email</span>
                                    <span className="vp-info-value">{partnerData?.email || "No Data"}</span>
                                </div>
                                <div className="vp-info-item">
                                    <span className="vp-info-label">Contact Number</span>
                                    <span className="vp-info-value">
                                        {partnerData?.country_code && partnerData?.contact_number
                                            ? `${partnerData.country_code} ${partnerData.contact_number}`
                                            : partnerData?.contact_number || "No Data"}
                                    </span>
                                </div>
                                <div className="vp-info-item">
                                    <span className="vp-info-label">Partner ID</span>
                                    <span className="vp-info-value">{partnerData?.staff_id || "No Data"}</span>
                                </div>
                                <div className="vp-info-item">
                                    <span className="vp-info-label">Role</span>
                                    <span className="vp-info-value">{roleLabel(partnerData?.role)}</span>
                                </div>
                                {/* <div className="vp-info-item">
                                    <span className="vp-info-label">Status</span>
                                    <span className={`vp-inline-status vp-inline-status--${partnerData?.status?.toLowerCase()}`}>
                                        {ToFirstCapital(partnerData?.account_status)}
                                    </span>
                                </div> */}
                                {/* <div className="vp-info-item">
                                    <span className="vp-info-label">Profile Status</span>
                                    <span className="vp-info-value">{ToFirstCapital(partnerData?.profile_status) || "No Data"}</span>
                                </div> */}
                                <div className="vp-info-item">
                                    <span className="vp-info-label">Created At</span>
                                    <span className="vp-info-value">{DateToLocale(partnerData?.created_at) || "No Data"}</span>
                                </div>
                            </div>
                        </div>

                        {/* Update Status */}
                        <div className="vp-card">
                            <div className="vp-card-header">
                                <span className="vp-card-badge">02</span>
                                <h2 className="vp-card-title">Update Status</h2>
                            </div>

                            <div className="vp-status-panel">
                                <div className="vp-status-field">
                                    <label className="vp-info-label">Select Status</label>
                                    <select
                                        className="vp-status-select"
                                        value={statusValue}
                                        onChange={handleStatusSelect}
                                        disabled={statusUpdating}
                                    >
                                        <option value="">Select status</option>
                                        <option value="approved">Approved</option>
                                        <option value="rejected">Rejected</option>
                                    </select>
                                </div>

                                {showBlockReason && (
                                    <div className="vp-status-field">
                                        <label className="vp-info-label">
                                            Block Reason <span style={{ color: "#ef4444" }}>*</span>
                                        </label>
                                        <textarea
                                            className="vp-status-textarea"
                                            value={blockReason}
                                            onChange={(e) => setBlockReason(e.target.value)}
                                            placeholder="Enter the reason for blocking..."
                                            rows={3}
                                            disabled={statusUpdating}
                                        />
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="vp-status-btn"
                                    onClick={handleStatusUpdate}
                                    disabled={statusUpdating || !statusValue || statusValue === partnerData?.account_status}
                                >
                                    {statusUpdating ? "Updating..." : "Update Status"}
                                </button>
                            </div>
                        </div>

                        {/* Bank Details */}
                        {/* {partnerData?.bank_details && (
                            <div className="vp-card">
                                <div className="vp-card-header">
                                    <span className="vp-card-badge">03</span>
                                    <h2 className="vp-card-title">Bank Details</h2>
                                </div>
                                <div className="vp-info-grid">
                                    <div className="vp-info-item">
                                        <span className="vp-info-label">Account Name</span>
                                        <span className="vp-info-value">{partnerData.bank_details?.account_name || "No Data"}</span>
                                    </div>
                                    <div className="vp-info-item">
                                        <span className="vp-info-label">Account Number</span>
                                        <span className="vp-info-value">{partnerData.bank_details?.account_number || "No Data"}</span>
                                    </div>
                                    <div className="vp-info-item">
                                        <span className="vp-info-label">Bank Name</span>
                                        <span className="vp-info-value">{partnerData.bank_details?.bank_name || "No Data"}</span>
                                    </div>
                                    <div className="vp-info-item">
                                        <span className="vp-info-label">IFSC Code</span>
                                        <span className="vp-info-value">{partnerData.bank_details?.ifsc_code || "No Data"}</span>
                                    </div>
                                </div>
                            </div>
                        )} */}

                        {/* Location */}
                        {/* {partnerData?.location?.coordinates?.length === 2 &&
                            (partnerData.location.coordinates[0] !== 0 || partnerData.location.coordinates[1] !== 0) && (
                                <div className="vp-card">
                                    <div className="vp-card-header">
                                        <span className="vp-card-badge">04</span>
                                        <h2 className="vp-card-title">Location</h2>
                                    </div>
                                    <div className="vp-coords-row" style={{ marginBottom: 12 }}>
                                        <div className="vp-coord-chip">
                                            <span className="vp-coord-label">LAT</span>
                                            <span className="vp-coord-value">
                                                {partnerData.location.coordinates[1].toFixed(6)}
                                            </span>
                                        </div>
                                        <div className="vp-coord-chip">
                                            <span className="vp-coord-label">LNG</span>
                                            <span className="vp-coord-value">
                                                {partnerData.location.coordinates[0].toFixed(6)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="vp-map-wrap">
                                        <iframe
                                            title="Partner Location"
                                            className="vp-map-iframe"
                                            loading="lazy"
                                            allowFullScreen
                                            src={`https://maps.google.com/maps?q=${partnerData.location.coordinates[1]},${partnerData.location.coordinates[0]}&z=15&output=embed`}
                                        />
                                    </div>
                                </div>
                            )} */}
                    </div>

                    {/* Right Column */}
                    <div className="vp-col">

                        {/* Documents */}
                        {partnerData?.documents?.length > 0 && (
                            <div className="vp-card">
                                <div className="vp-card-header">
                                    <span className="vp-card-badge">02</span>
                                    <h2 className="vp-card-title">Documents</h2>
                                </div>
                                <div className="vp-doc-list">
                                    {partnerData.documents.map((doc, idx) => (
                                        <div className="vp-doc-item" key={idx}>
                                            <div className="vp-doc-icon">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                    <polyline points="14 2 14 8 20 8" />
                                                </svg>
                                            </div>
                                            <div className="vp-doc-info">
                                                <div className="vp-doc-name">{ToFirstCapital(doc.type) || `Document ${idx + 1}`}</div>
                                                {doc.url && (
                                                    <a href={doc.url} target="_blank" rel="noreferrer" className="vp-doc-link">
                                                        View Document →
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Address */}
                        {partnerData?.address && (
                            <div className="vp-card">
                                <div className="vp-card-header">
                                    <span className="vp-card-badge">03</span>
                                    <h2 className="vp-card-title">Address</h2>
                                </div>
                                <div className="vp-address-item">
                                    <div className="vp-address-icon">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                    </div>
                                    <div>
                                        <div className="vp-address-label">Full Address</div>
                                        <div className="vp-address-val">{partnerData.address}</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Referral Info */}
                        {partnerData?.referred_by && (
                            <div className="vp-card">
                                <div className="vp-card-header">
                                    <span className="vp-card-badge">06</span>
                                    <h2 className="vp-card-title">Referred By</h2>
                                </div>
                                <div className="vp-info-grid">
                                    <div className="vp-info-item">
                                        <span className="vp-info-label">Name</span>
                                        <span className="vp-info-value">{partnerData.referred_by?.name || "No Data"}</span>
                                    </div>
                                    <div className="vp-info-item">
                                        <span className="vp-info-label">Email</span>
                                        <span className="vp-info-value">{partnerData.referred_by?.email || "No Data"}</span>
                                    </div>
                                    <div className="vp-info-item">
                                        <span className="vp-info-label">Contact</span>
                                        <span className="vp-info-value">{partnerData.referred_by?.contact_number || "No Data"}</span>
                                    </div>
                                    <div className="vp-info-item">
                                        <span className="vp-info-label">Role</span>
                                        <span className="vp-info-value">{roleLabel(partnerData.referred_by?.role)}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Margin Details */}
                        {partnerData?.margin_detail?.length > 0 && (
                            <div className="vp-card">
                                <div className="vp-card-header">
                                    <span className="vp-card-badge">07</span>
                                    <h2 className="vp-card-title">Margin Details</h2>
                                </div>
                                <div className="vp-margin-list">
                                    {partnerData.margin_detail.map((margin, idx) => (
                                        <div className="vp-margin-item" key={margin._id || idx}>
                                            <div className="vp-margin-header">
                                                <span className="vp-margin-role">{roleLabel(margin.role)}</span>
                                                <span className="vp-margin-lead">Lead: {ToFirstCapital(margin.lead_from)}</span>
                                            </div>
                                            <div className="vp-margin-bars">
                                                <div className="vp-margin-row">
                                                    <span className="vp-margin-label">Agent Margin</span>
                                                    <div className="vp-margin-track">
                                                        <div className="vp-margin-fill vp-margin-fill--agent" style={{ width: `${margin.agent_margin}%` }} />
                                                    </div>
                                                    <span className="vp-margin-pct">{margin.agent_margin}%</span>
                                                </div>
                                                <div className="vp-margin-row">
                                                    <span className="vp-margin-label">Admin Margin</span>
                                                    <div className="vp-margin-track">
                                                        <div className="vp-margin-fill vp-margin-fill--admin" style={{ width: `${margin.admin_margin}%` }} />
                                                    </div>
                                                    <span className="vp-margin-pct">{margin.admin_margin}%</span>
                                                </div>
                                                <div className="vp-margin-row">
                                                    <span className="vp-margin-label">Other Margin</span>
                                                    <div className="vp-margin-track">
                                                        <div className="vp-margin-fill vp-margin-fill--other" style={{ width: `${margin.other_margin}%` }} />
                                                    </div>
                                                    <span className="vp-margin-pct">{margin.other_margin}%</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default ViewPartner;